import { detectPitch } from './pitch-detector.js';
import { MusicTheory } from './music-theory.js';
import { GameEngine } from './game-engine.js';
import { SongLibrary } from './song-library.js';
import { Visualizer } from './visualizer.js';

class SingingTutorApp {
    constructor() {
        this.audioContext = null;
        this.analyser = null;
        this.microphone = null;
        this.micGain = null;
        this.isListening = false;
        this.animationId = null;
        this.microphoneGranted = false;
        
        // Audio playback (separate from microphone context)
        this.playbackContext = new (window.AudioContext || window.webkitAudioContext)();
        this.currentOscillator = null;
        
        this.musicTheory = new MusicTheory();
        this.gameEngine = new GameEngine();
        this.songLibrary = new SongLibrary();
        this.visualizer = new Visualizer('pitch-visualizer');
        
        this.currentMode = null;
        this.targetNote = null;
        this.currentInterval = null;
        this.listeningForAnswer = false;
        
        // Vocal range tracking
        this.vocalRange = this.loadVocalRange();
        this.voiceCatcherGame = null;
        
        this.initializeEventListeners();
        this.showMicrophonePermissionModal();
        this.loadAchievements();
        
        // Show vocal range if it exists
        if (this.vocalRange) {
            this.updateVocalRangeDisplay();
        }
    }

    showMicrophonePermissionModal() {
        const modal = document.getElementById('mic-permission-modal');
        const grantButton = document.getElementById('grant-mic-permission');
        
        grantButton.addEventListener('click', async () => {
            try {
                await this.requestMicrophonePermission();
                modal.classList.add('hidden');
                
                // Show voice catcher game if no vocal range is saved
                if (!this.vocalRange) {
                    this.startVoiceCatcherGame();
                }
            } catch (error) {
                console.error('Failed to get microphone permission:', error);
            }
        });
    }

    async requestMicrophonePermission() {
        try {
            await this.startMicrophone();
            this.microphoneGranted = true;
            console.log('Microphone permission granted and initialized');
        } catch (error) {
            alert('⚠️ Microphone access is required for this app to work. Please allow microphone access and reload the page.');
            throw error;
        }
    }

    loadVocalRange() {
        const saved = localStorage.getItem('vocalRange');
        return saved ? JSON.parse(saved) : null;
    }

    saveVocalRange(range) {
        this.vocalRange = range;
        localStorage.setItem('vocalRange', JSON.stringify(range));
    }

    startVoiceCatcherGame() {
        const gameScreen = document.getElementById('voice-catcher-game');
        gameScreen.classList.remove('hidden');
        
        this.voiceCatcherGame = new VoiceCatcherGame(
            this.audioContext,
            this.analyser,
            this.musicTheory,
            (range) => {
                // Callback when game completes
                this.saveVocalRange(range);
                this.showRangeResults(range);
            }
        );
        
        this.voiceCatcherGame.start();
    }

    showRangeResults(range) {
        const gameComplete = document.getElementById('game-complete');
        const discoveredRange = document.getElementById('discovered-range');
        const rangeSemitones = document.getElementById('range-semitones');
        const rangeDescription = document.getElementById('range-description');
        
        gameComplete.classList.remove('hidden');
        discoveredRange.textContent = `${range.lowest} → ${range.highest}`;
        rangeSemitones.textContent = range.semitones;
        
        // Provide encouraging feedback based on range
        if (range.semitones >= 24) {
            rangeDescription.textContent = "🌟 Wow! You have an amazing wide vocal range!";
        } else if (range.semitones >= 16) {
            rangeDescription.textContent = "🎵 Great range! Perfect for learning songs!";
        } else if (range.semitones >= 10) {
            rangeDescription.textContent = "👍 Nice! You have a good comfortable range!";
        } else {
            rangeDescription.textContent = "🎤 Good start! Your range will grow with practice!";
        }
        
        // Setup completion buttons
        document.getElementById('start-app').onclick = () => {
            document.getElementById('voice-catcher-game').classList.add('hidden');
            if (this.voiceCatcherGame) {
                this.voiceCatcherGame.stop();
            }
            this.updateVocalRangeDisplay();
        };
        
        document.getElementById('play-again').onclick = () => {
            gameComplete.classList.add('hidden');
            this.voiceCatcherGame.reset();
            this.voiceCatcherGame.start();
        };
    }

    updateVocalRangeDisplay() {
        if (this.vocalRange) {
            const rangeDisplay = document.getElementById('vocal-range-display');
            const rangeText = document.getElementById('vocal-range-text');
            const recalibrateBtn = document.getElementById('recalibrate-range-btn');
            
            rangeDisplay.style.display = 'inline';
            rangeText.style.display = 'inline';
            rangeText.textContent = `${this.vocalRange.lowest}-${this.vocalRange.highest}`;
            recalibrateBtn.style.display = 'block';
        }
    }

    initializeEventListeners() {
        // Recalibrate vocal range button
        document.getElementById('recalibrate-range-btn').addEventListener('click', () => {
            this.startVoiceCatcherGame();
        });
        
        // Menu buttons
        document.querySelectorAll('.menu-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const mode = e.currentTarget.dataset.mode;
                
                // Check if microphone permission was granted
                if (!this.microphoneGranted) {
                    alert('Please allow microphone access first!');
                    document.getElementById('mic-permission-modal').classList.remove('hidden');
                    return;
                }
                
                // Ensure microphone is running
                if (!this.isListening) {
                    try {
                        await this.startMicrophone();
                    } catch (error) {
                        console.error('Failed to start microphone:', error);
                        return;
                    }
                }
                
                // Then start the selected mode
                this.startMode(mode);
            });
        });

        // Back to menu
        document.getElementById('back-to-menu').addEventListener('click', () => {
            this.backToMenu();
        });

        // Song filters
        document.querySelectorAll('.filter-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
                e.currentTarget.classList.add('active');
                const lang = e.currentTarget.dataset.lang;
                this.filterSongs(lang);
            });
        });
        
        // Play sound button
        document.getElementById('play-target').addEventListener('click', () => {
            this.playTargetSound();
        });
    }

    async startMode(mode) {
        this.currentMode = mode;
        
        // Hide menu, show practice screen
        document.getElementById('main-menu').classList.add('hidden');
        document.getElementById('practice-screen').classList.add('active');
        
        switch(mode) {
            case 'notes':
                this.startNoteRecognition();
                break;
            case 'intervals':
                this.startIntervalPractice();
                break;
            case 'scales':
                this.startScalePractice();
                break;
            case 'songs':
                this.startSongMode();
                break;
            case 'rhythm':
                this.startRhythmPractice();
                break;
        }
    }

    startNoteRecognition() {
        document.getElementById('exercise-title').textContent = 'Learn Notes';
        document.getElementById('exercise-instructions').textContent = 
            'Listen to the note, then sing it back!';
        document.getElementById('target-display').classList.remove('hidden');
        document.getElementById('song-player').classList.add('hidden');
        
        this.listeningForAnswer = false;
        this.generateRandomNote();
        
        // Auto-play the note when starting
        setTimeout(() => this.playTargetSound(), 500);
    }

    startIntervalPractice() {
        document.getElementById('exercise-title').textContent = 'Practice Intervals';
        document.getElementById('exercise-instructions').textContent = 
            'Listen to both notes, then sing the second note!';
        document.getElementById('target-display').classList.remove('hidden');
        document.getElementById('song-player').classList.add('hidden');
        
        this.listeningForAnswer = false;
        this.generateRandomInterval();
        
        // Auto-play the interval when starting
        setTimeout(() => this.playTargetSound(), 500);
    }

    startScalePractice() {
        document.getElementById('exercise-title').textContent = 'Scale Practice';
        document.getElementById('exercise-instructions').textContent = 'Sing the scale!';
        document.getElementById('target-display').classList.remove('hidden');
        document.getElementById('song-player').classList.add('hidden');
        
        this.generateRandomScale();
    }

    startSongMode() {
        document.getElementById('exercise-title').textContent = 'Sing a Song!';
        document.getElementById('exercise-instructions').textContent = 'Choose a song to practice';
        document.getElementById('target-display').classList.add('hidden');
        
        this.showSongSelection();
    }

    startRhythmPractice() {
        document.getElementById('exercise-title').textContent = 'Rhythm Practice';
        document.getElementById('exercise-instructions').textContent = 'Match the rhythm!';
        document.getElementById('target-display').classList.remove('hidden');
        document.getElementById('song-player').classList.add('hidden');
        
        this.generateRhythmExercise();
    }

    generateRandomNote() {
        let targetNote;
        
        if (this.vocalRange) {
            // Generate note within user's comfortable vocal range
            const lowestFreq = this.vocalRange.lowestFreq;
            const highestFreq = this.vocalRange.highestFreq;
            
            // Get all notes in their range
            const notesInRange = [];
            const allNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
            
            for (let octave = 2; octave <= 6; octave++) {
                for (let note of allNotes) {
                    const noteName = note + octave;
                    const freq = this.musicTheory.noteToFrequency(noteName);
                    if (freq >= lowestFreq && freq <= highestFreq) {
                        notesInRange.push(noteName);
                    }
                }
            }
            
            // Pick a random note from their range
            if (notesInRange.length > 0) {
                targetNote = notesInRange[Math.floor(Math.random() * notesInRange.length)];
            } else {
                // Fallback to default range
                targetNote = this.getDefaultRandomNote();
            }
        } else {
            // No vocal range calibrated - use default range
            targetNote = this.getDefaultRandomNote();
        }
        
        this.targetNote = targetNote;
        document.getElementById('target-note').textContent = this.targetNote;
    }
    
    getDefaultRandomNote() {
        const notes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
        const octaves = [3, 4, 5];
        const note = notes[Math.floor(Math.random() * notes.length)];
        const octave = octaves[Math.floor(Math.random() * octaves.length)];
        return note + octave;
    }

    generateRandomInterval() {
        const intervals = this.musicTheory.getIntervals();
        // Filter to common singing intervals for kids
        const commonIntervals = intervals.filter(i => 
            i.semitones >= 0 && i.semitones <= 12 && i.semitones !== 6
        );
        
        let baseNote, targetNote, interval;
        let attempts = 0;
        const maxAttempts = 50;
        
        // Try to generate an interval within the user's vocal range
        do {
            interval = commonIntervals[Math.floor(Math.random() * commonIntervals.length)];
            
            if (this.vocalRange) {
                // Start from a note within their range
                const lowestFreq = this.vocalRange.lowestFreq;
                const highestFreq = this.vocalRange.highestFreq;
                const midFreq = Math.sqrt(lowestFreq * highestFreq); // Geometric mean
                
                baseNote = this.musicTheory.frequencyToNote(midFreq);
                const baseFreq = this.musicTheory.noteToFrequency(baseNote);
                const targetFreq = baseFreq * Math.pow(2, interval.semitones / 12);
                targetNote = this.musicTheory.frequencyToNote(targetFreq);
                
                // Check if target note is within range
                if (targetFreq >= lowestFreq && targetFreq <= highestFreq) {
                    break; // Found a good interval!
                }
            } else {
                // No vocal range - use default behavior
                baseNote = this.musicTheory.getRandomNote();
                const baseFreq = this.musicTheory.noteToFrequency(baseNote);
                const targetFreq = baseFreq * Math.pow(2, interval.semitones / 12);
                targetNote = this.musicTheory.frequencyToNote(targetFreq);
                break;
            }
            
            attempts++;
        } while (attempts < maxAttempts);
        
        // Fallback if we couldn't find a good interval
        if (attempts >= maxAttempts) {
            baseNote = this.getDefaultRandomNote();
            const baseFreq = this.musicTheory.noteToFrequency(baseNote);
            const targetFreq = baseFreq * Math.pow(2, interval.semitones / 12);
            targetNote = this.musicTheory.frequencyToNote(targetFreq);
        }
        
        this.currentInterval = {
            baseNote: baseNote,
            targetNote: targetNote,
            interval: interval
        };
        
        this.targetNote = targetNote;
        document.getElementById('target-note').textContent = 
            `${baseNote} → ${interval.name} → ${targetNote}`;
    }

    generateRandomScale() {
        const scales = this.musicTheory.getScales();
        const scale = scales[Math.floor(Math.random() * scales.length)];
        
        document.getElementById('target-note').textContent = scale.name;
    }

    generateRhythmExercise() {
        const rhythms = this.musicTheory.getRhythms();
        const rhythm = rhythms[Math.floor(Math.random() * rhythms.length)];
        
        document.getElementById('target-note').textContent = rhythm.name;
    }

    async toggleMicrophone() {
        if (!this.isListening) {
            await this.startMicrophone();
        } else {
            this.stopMicrophone();
        }
    }

    async startMicrophone() {
        try {
            console.log('Requesting microphone access...');
            
            // Request microphone permission
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false  // We'll implement our own
                } 
            });
            
            console.log('Microphone access granted!');
            
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            console.log('AudioContext created, state:', this.audioContext.state);
            
            // Resume context if suspended (some browsers require this)
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
                console.log('AudioContext resumed');
            }
            
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 2048;
            
            // Create microphone input with auto gain control
            this.microphone = this.audioContext.createMediaStreamSource(stream);
            
            // Add gain node with initial value
            this.micGain = this.audioContext.createGain();
            this.micGain.gain.value = 2.0; // Start with moderate gain
            
            // Auto gain control settings
            this.targetRMS = 0.1;  // Target RMS level for singing
            this.minGain = 1.0;
            this.maxGain = 10.0;
            this.gainAdjustmentSpeed = 0.02; // Slower adjustment (was 0.05)
            
            // Noise detection and singing level tracking
            this.noiseFloor = 0.01; // Initial noise floor estimate
            this.singingLevelHistory = []; // Track singing levels over time
            this.singingLevelWindowSize = 120; // 2 seconds at 60fps
            this.noiseFloorHistory = [];
            this.noiseFloorWindowSize = 300; // 5 seconds for noise floor estimation
            
            // Connect: microphone → gain → analyser
            this.microphone.connect(this.micGain);
            this.micGain.connect(this.analyser);
            
            console.log('Audio chain connected: microphone → gain(3x) → analyser');
            
            this.isListening = true;
            
            console.log('Starting pitch detection...');
            this.startPitchDetection();
        } catch (error) {
            console.error('Error accessing microphone:', error);
            if (error.name === 'NotAllowedError') {
                alert('⚠️ Microphone permission denied. Please allow microphone access in your browser settings and try again.');
            } else if (error.name === 'NotFoundError') {
                alert('⚠️ No microphone found. Please connect a microphone and try again.');
            } else {
                alert('⚠️ Could not access microphone: ' + error.message);
            }
        }
    }

    stopMicrophone() {
        if (this.microphone) {
            this.microphone.disconnect();
            this.microphone = null;
        }
        
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        
        this.isListening = false;
        
        document.getElementById('detected-note').textContent = '--';
        document.getElementById('detected-freq').textContent = '-- Hz';
        this.visualizer.clear();
    }

    startPitchDetection() {
        const bufferLength = this.analyser.fftSize;
        const buffer = new Float32Array(bufferLength);
        
        console.log('Pitch detection started with buffer length:', bufferLength);
        console.log('Sample rate:', this.audioContext.sampleRate);
        console.log('Auto gain control enabled');
        
        let frameCount = 0;
        
        const detect = () => {
            if (!this.isListening) return;
            
            this.analyser.getFloatTimeDomainData(buffer);
            
            // Calculate RMS for level detection
            let rms = 0;
            for (let i = 0; i < buffer.length; i++) {
                rms += buffer[i] * buffer[i];
            }
            rms = Math.sqrt(rms / buffer.length);
            
            // Detect pitch first to determine if user is singing
            const [pitch, clarity] = detectPitch(buffer, this.audioContext.sampleRate);
            const isSinging = pitch !== null && clarity > 0.7; // Lower threshold for detection
            
            // Update noise floor (only when NOT singing)
            if (!isSinging && rms > 0.0001) {
                this.noiseFloorHistory.push(rms);
                if (this.noiseFloorHistory.length > this.noiseFloorWindowSize) {
                    this.noiseFloorHistory.shift();
                }
                // Noise floor is the average of quiet moments
                this.noiseFloor = this.noiseFloorHistory.reduce((a, b) => a + b, 0) / this.noiseFloorHistory.length;
            }
            
            // Track singing levels (only when actually singing)
            if (isSinging && rms > this.noiseFloor * 2) {
                this.singingLevelHistory.push(rms);
                if (this.singingLevelHistory.length > this.singingLevelWindowSize) {
                    this.singingLevelHistory.shift();
                }
            }
            
            // Auto gain control - only adjust based on actual singing levels
            if (this.singingLevelHistory.length > 10) { // Need some singing samples first
                // Calculate average singing level over the window
                const avgSingingLevel = this.singingLevelHistory.reduce((a, b) => a + b, 0) / this.singingLevelHistory.length;
                
                const currentGain = this.micGain.gain.value;
                let targetGain = currentGain;
                
                // Only adjust if significantly different from target
                if (avgSingingLevel < this.targetRMS * 0.4) {
                    // Singing is too quiet, increase gain
                    targetGain = Math.min(currentGain * (1 + this.gainAdjustmentSpeed), this.maxGain);
                } else if (avgSingingLevel > this.targetRMS * 2.0) {
                    // Singing is too loud, decrease gain
                    targetGain = Math.max(currentGain * (1 - this.gainAdjustmentSpeed), this.minGain);
                }
                
                if (Math.abs(targetGain - currentGain) > 0.05) {
                    this.micGain.gain.setValueAtTime(targetGain, this.audioContext.currentTime);
                    
                    if (frameCount % 60 === 0) {
                        console.log(`AGC: ${currentGain.toFixed(2)} → ${targetGain.toFixed(2)} | Singing avg: ${avgSingingLevel.toFixed(4)} | Noise: ${this.noiseFloor.toFixed(4)}`);
                    }
                }
            }
            
            // Log levels every 60 frames (~1 second)
            if (frameCount % 60 === 0) {
                const status = isSinging ? '🎤 Singing' : '🔇 Quiet';
                console.log(`${status} | RMS: ${rms.toFixed(4)} | Noise floor: ${this.noiseFloor.toFixed(4)} | Gain: ${this.micGain.gain.value.toFixed(2)} | History: ${this.singingLevelHistory.length}`);
            }
            frameCount++;
            
            // Lower clarity threshold to match AGC tracking (0.7 instead of 0.9)
            if (pitch && clarity > 0.7) {
                const note = this.musicTheory.frequencyToNote(pitch);
                
                console.log(`Detected: ${note} (${pitch.toFixed(1)} Hz, clarity: ${clarity.toFixed(2)})`);
                
                document.getElementById('detected-note').textContent = note;
                document.getElementById('detected-freq').textContent = `${pitch.toFixed(1)} Hz`;
                
                // Update visualizer
                this.visualizer.update(pitch, note, this.targetNote);
                
                // Check accuracy (only when listening for answer)
                if (this.targetNote && this.listeningForAnswer) {
                    this.checkAccuracy(note);
                }
            } else {
                this.visualizer.updateWaveform(buffer);
            }
            
            this.animationId = requestAnimationFrame(detect);
        };
        
        detect();
    }

    checkAccuracy(detectedNote) {
        // Only check if we're listening for an answer
        if (!this.listeningForAnswer) return;
        
        const feedback = document.getElementById('accuracy-feedback');
        
        if (detectedNote === this.targetNote) {
            // Temporarily stop listening to avoid multiple triggers
            this.listeningForAnswer = false;
            
            feedback.textContent = '🎉 Perfect! Amazing job!';
            feedback.className = 'feedback perfect';
            this.gameEngine.addPoints(100);
            this.celebrateSuccess();
        } else if (this.musicTheory.isClose(detectedNote, this.targetNote)) {
            feedback.textContent = '👍 Close! Keep trying!';
            feedback.className = 'feedback close';
            this.gameEngine.addPoints(20);
            // Keep listening for better attempts
        } else {
            feedback.textContent = '🎯 Try again!';
            feedback.className = 'feedback off';
            // Keep listening
        }
        
        this.updateScore();
    }
    
    playTargetSound() {
        // Stop any currently playing sound
        this.stopSound();
        
        // Stop listening while the note plays to avoid catching the playback
        this.listeningForAnswer = false;
        
        if (this.currentMode === 'intervals' && this.currentInterval) {
            // Play interval: base note, pause, target note
            document.getElementById('accuracy-feedback').textContent = 
                '🎧 Listen carefully...';
            
            this.playNote(this.currentInterval.baseNote, 0.8);
            setTimeout(() => {
                this.playNote(this.currentInterval.targetNote, 0.8);
                setTimeout(() => {
                    // Start listening only after both notes finish (800ms + 200ms buffer)
                    this.listeningForAnswer = true;
                    document.getElementById('accuracy-feedback').textContent = 
                        '🎤 Now you try! Take your time.';
                }, 1000); // Wait for full note duration + buffer
            }, 1000);
        } else if (this.targetNote) {
            // Play single note
            document.getElementById('accuracy-feedback').textContent = 
                '🎧 Listen...';
            
            this.playNote(this.targetNote, 1.5);
            
            // Start listening only after the note finishes playing (1500ms + 300ms buffer)
            setTimeout(() => {
                this.listeningForAnswer = true;
                document.getElementById('accuracy-feedback').textContent = 
                    '🎤 Sing it back! Take your time.';
            }, 1800);
        }
    }
    
    playNote(noteName, duration = 1.0) {
        const frequency = this.musicTheory.noteToFrequency(noteName);
        if (!frequency) return;
        
        const oscillator = this.playbackContext.createOscillator();
        const gainNode = this.playbackContext.createGain();
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, this.playbackContext.currentTime);
        
        // Envelope for smooth sound
        gainNode.gain.setValueAtTime(0, this.playbackContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, this.playbackContext.currentTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0.2, this.playbackContext.currentTime + duration - 0.1);
        gainNode.gain.linearRampToValueAtTime(0, this.playbackContext.currentTime + duration);
        
        oscillator.connect(gainNode);
        gainNode.connect(this.playbackContext.destination);
        
        oscillator.start(this.playbackContext.currentTime);
        oscillator.stop(this.playbackContext.currentTime + duration);
        
        this.currentOscillator = oscillator;
    }
    
    stopSound() {
        if (this.currentOscillator) {
            try {
                this.currentOscillator.stop();
            } catch (e) {
                // Already stopped
            }
            this.currentOscillator = null;
        }
    }

    celebrateSuccess() {
        // Visual celebration
        this.visualizer.celebrate();
        
        // Stop listening for answer
        this.listeningForAnswer = false;
        
        // Generate new target after a short delay
        setTimeout(() => {
            if (this.currentMode === 'notes') {
                this.generateRandomNote();
                setTimeout(() => this.playTargetSound(), 500);
            } else if (this.currentMode === 'intervals') {
                this.generateRandomInterval();
                setTimeout(() => this.playTargetSound(), 500);
            } else if (this.currentMode === 'scales') {
                this.generateRandomScale();
            }
            
            document.getElementById('accuracy-feedback').textContent = '';
        }, 2000);
    }

    updateScore() {
        document.getElementById('score').textContent = this.gameEngine.getScore();
        document.getElementById('level').textContent = this.gameEngine.getLevel();
    }

    showSongSelection() {
        document.getElementById('practice-screen').classList.remove('active');
        document.getElementById('song-selection').classList.remove('hidden');
        
        this.renderSongList();
    }

    renderSongList(filter = 'all') {
        const songList = document.getElementById('song-list');
        const songs = this.songLibrary.getSongs(filter);
        
        songList.innerHTML = songs.map(song => `
            <div class="song-card" data-song-id="${song.id}">
                <h3>${song.title}</h3>
                <p class="metadata">
                    ${song.language === 'en' ? '🇬🇧' : '🇷🇺'} ${song.author}<br>
                    ${song.epoch} • ${song.theme}
                </p>
            </div>
        `).join('');
        
        // Add click listeners
        document.querySelectorAll('.song-card').forEach(card => {
            card.addEventListener('click', (e) => {
                const songId = e.currentTarget.dataset.songId;
                this.playSong(songId);
            });
        });
    }

    filterSongs(lang) {
        this.renderSongList(lang);
    }

    playSong(songId) {
        document.getElementById('song-selection').classList.add('hidden');
        document.getElementById('practice-screen').classList.add('active');
        document.getElementById('song-player').classList.remove('hidden');
        
        const song = this.songLibrary.getSongById(songId);
        document.getElementById('song-title').textContent = song.title;
        document.getElementById('song-metadata').textContent = 
            `${song.author} • ${song.epoch}`;
        
        // Load and display lyrics
        document.getElementById('lyrics-display').innerHTML = 
            song.lyrics.map(line => `<p>${line}</p>`).join('');
    }

    backToMenu() {
        this.stopMicrophone();
        this.stopSound();
        document.getElementById('practice-screen').classList.remove('active');
        document.getElementById('song-selection').classList.add('hidden');
        document.getElementById('main-menu').classList.remove('hidden');
        document.getElementById('main-menu').classList.add('active');
        
        this.currentMode = null;
        this.targetNote = null;
        this.currentInterval = null;
        this.listeningForAnswer = false;
    }

    loadAchievements() {
        const achievements = this.gameEngine.getAchievements();
        const badgesContainer = document.getElementById('achievement-badges');
        
        achievements.forEach(achievement => {
            const badge = document.createElement('div');
            badge.className = 'badge';
            badge.textContent = `${achievement.icon} ${achievement.name}`;
            badgesContainer.appendChild(badge);
        });
    }
}

// Voice Catcher Game - fun way to discover vocal range
class VoiceCatcherGame {
    constructor(audioContext, analyser, musicTheory, onComplete) {
        this.audioContext = audioContext;
        this.analyser = analyser;
        this.musicTheory = musicTheory;
        this.onComplete = onComplete;
        
        this.canvas = document.getElementById('voice-catcher-canvas');
        this.ctx = this.canvas.getContext('2d');
        this.running = false;
        this.animationId = null;
        
        // Game state
        this.caughtNotes = [];
        this.targetCatchCount = 12;
        this.streak = 0;
        this.maxStreak = 0;
        this.bubbles = [];
        this.lastCaughtNote = null;
        this.lastCaughtTime = 0;
        
        // Setup canvas
        this.setupCanvas();
        this.generateBubbles();
    }
    
    setupCanvas() {
        const rect = this.canvas.getBoundingClientRect();
        this.canvas.width = rect.width * window.devicePixelRatio;
        this.canvas.height = rect.height * window.devicePixelRatio;
        this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        this.canvasWidth = rect.width;
        this.canvasHeight = rect.height;
    }
    
    generateBubbles() {
        // Create bubbles for notes spanning a wide range (C3 to C6)
        const notes = [];
        const baseOctaves = [3, 4, 5];
        const noteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
        
        baseOctaves.forEach(octave => {
            noteNames.forEach(name => {
                notes.push(`${name}${octave}`);
            });
        });
        
        // Create floating bubbles
        this.bubbles = notes.map((note, index) => {
            const freq = this.musicTheory.noteToFrequency(note);
            // Position bubbles based on frequency (higher = higher on screen)
            const normalizedFreq = (freq - 130) / (1047 - 130); // C3 to C6
            const y = this.canvasHeight * (1 - normalizedFreq * 0.8) - 20;
            
            return {
                note: note,
                freq: freq,
                x: (index % 7) * (this.canvasWidth / 7) + this.canvasWidth / 14,
                y: y,
                radius: 25,
                caught: false,
                wobble: Math.random() * Math.PI * 2,
                wobbleSpeed: 0.02 + Math.random() * 0.03,
                floatOffset: 0
            };
        });
    }
    
    start() {
        this.running = true;
        this.detectAndCatch();
        this.animate();
    }
    
    stop() {
        this.running = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    }
    
    reset() {
        this.caughtNotes = [];
        this.streak = 0;
        this.bubbles.forEach(b => b.caught = false);
        this.updateStats();
    }
    
    detectAndCatch() {
        if (!this.running) return;
        
        const bufferLength = this.analyser.fftSize;
        const buffer = new Float32Array(bufferLength);
        this.analyser.getFloatTimeDomainData(buffer);
        
        const [pitch, clarity] = detectPitch(buffer, this.audioContext.sampleRate);
        
        if (pitch && clarity > 0.75) {
            const detectedNote = this.musicTheory.frequencyToNote(pitch);
            
            // Find matching bubble
            const bubble = this.bubbles.find(b => 
                !b.caught && b.note === detectedNote
            );
            
            if (bubble) {
                // Catch the bubble!
                this.catchBubble(bubble, detectedNote);
            }
        }
        
        requestAnimationFrame(() => this.detectAndCatch());
    }
    
    catchBubble(bubble, note) {
        const now = Date.now();
        
        // Prevent duplicate catches
        if (note === this.lastCaughtNote && now - this.lastCaughtTime < 1000) {
            return;
        }
        
        bubble.caught = true;
        this.caughtNotes.push(note);
        this.lastCaughtNote = note;
        this.lastCaughtTime = now;
        
        // Update streak
        this.streak++;
        this.maxStreak = Math.max(this.maxStreak, this.streak);
        
        this.updateStats();
        this.updateHints();
        
        // Check if game complete
        if (this.caughtNotes.length >= this.targetCatchCount) {
            this.completeGame();
        }
    }
    
    updateStats() {
        document.getElementById('notes-caught').textContent = this.caughtNotes.length;
        document.getElementById('catch-streak').textContent = this.streak;
    }
    
    updateHints() {
        const hintElement = document.getElementById('game-hint');
        const remaining = this.targetCatchCount - this.caughtNotes.length;
        
        if (remaining === 0) {
            hintElement.textContent = "🎉 Amazing! Analyzing your voice...";
        } else if (remaining <= 3) {
            hintElement.textContent = `Just ${remaining} more! You're doing great! 🌟`;
        } else if (this.streak >= 3) {
            hintElement.textContent = `🔥 On fire! ${this.streak} in a row!`;
        } else {
            const caughtLow = this.caughtNotes.some(n => n.includes('3'));
            const caughtHigh = this.caughtNotes.some(n => n.includes('5') || n.includes('6'));
            
            if (!caughtLow) {
                hintElement.textContent = "Try singing some lower notes! 🎵";
            } else if (!caughtHigh) {
                hintElement.textContent = "Try singing some higher notes! 🎶";
            } else {
                hintElement.textContent = `Great variety! ${remaining} more notes to go! ⭐`;
            }
        }
    }
    
    animate() {
        if (!this.running) return;
        
        // Clear canvas
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        
        // Draw bubbles
        this.bubbles.forEach(bubble => {
            // Animate wobble
            bubble.wobble += bubble.wobbleSpeed;
            bubble.floatOffset = Math.sin(bubble.wobble) * 3;
            
            const x = bubble.x;
            const y = bubble.y + bubble.floatOffset;
            
            if (bubble.caught) {
                // Caught bubble - golden and smaller
                this.ctx.fillStyle = 'rgba(250, 204, 21, 0.3)';
                this.ctx.strokeStyle = 'rgba(250, 204, 21, 0.8)';
                this.ctx.lineWidth = 2;
            } else {
                // Uncaught bubble - colorful gradient
                const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, bubble.radius);
                gradient.addColorStop(0, 'rgba(139, 92, 246, 0.4)');
                gradient.addColorStop(1, 'rgba(99, 102, 241, 0.2)');
                this.ctx.fillStyle = gradient;
                this.ctx.strokeStyle = 'rgba(139, 92, 246, 0.6)';
                this.ctx.lineWidth = 2;
            }
            
            // Draw bubble
            this.ctx.beginPath();
            this.ctx.arc(x, y, bubble.radius, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.stroke();
            
            // Draw note name
            this.ctx.fillStyle = bubble.caught ? '#ca8a04' : '#5b21b6';
            this.ctx.font = 'bold 14px -apple-system, sans-serif';
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(bubble.note, x, y);
        });
        
        this.animationId = requestAnimationFrame(() => this.animate());
    }
    
    completeGame() {
        this.stop();
        
        // Calculate vocal range from caught notes
        const frequencies = this.caughtNotes.map(note => 
            this.musicTheory.noteToFrequency(note)
        );
        
        const lowestFreq = Math.min(...frequencies);
        const highestFreq = Math.max(...frequencies);
        
        const lowestNote = this.musicTheory.frequencyToNote(lowestFreq);
        const highestNote = this.musicTheory.frequencyToNote(highestFreq);
        
        // Calculate semitones difference
        const semitones = Math.round(12 * Math.log2(highestFreq / lowestFreq));
        
        const range = {
            lowest: lowestNote,
            highest: highestNote,
            lowestFreq: lowestFreq,
            highestFreq: highestFreq,
            semitones: semitones,
            caughtCount: this.caughtNotes.length,
            maxStreak: this.maxStreak
        };
        
        this.onComplete(range);
    }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new SingingTutorApp();
});
